import moment from "moment";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import emojiRegex from 'emoji-regex';
import Config from "src/config";
import useAuth from "src/hooks/useAuth";
import { convertUrls } from "src/utils/functions";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "src/@/components/ui/dropdown-menu"
import { Button } from "src/@/components/ui/button";
import { Skeleton } from "src/@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import Iconify from "src/components/Iconify";
import { fToNow } from "src/utils/formatTime";

// ---------------------------------------------------------------------------
// Image loader with skeleton + error
// ---------------------------------------------------------------------------
const ImageWithLoading = ({ src, onClick, className, compact }) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    return (
        <div className="relative overflow-hidden rounded-xl">
            {isLoading && !error && (
                <Skeleton className={`w-full ${compact ? 'h-[120px]' : 'h-[220px]'} rounded-xl`} />
            )}
            {error ? (
                <div className={`flex flex-col items-center justify-center w-full rounded-xl bg-gray-50 dark:bg-[#1A1A1A] ${compact ? 'h-[80px]' : 'h-[180px]'}`}>
                    <AlertCircle className={`text-gray-300 dark:text-[#555] ${compact ? 'w-4 h-4 mb-1' : 'w-6 h-6 mb-1.5'}`} />
                    <p className={`text-gray-400 dark:text-[#71717A] ${compact ? 'text-[9px]' : 'text-[11px]'}`}>{t('chat.internal.message.imageLoadError')}</p>
                </div>
            ) : (
                <img
                    src={src}
                    className={`${className} ${isLoading ? 'opacity-0 absolute inset-0' : 'opacity-100 transition-opacity duration-300'}`}
                    onClick={onClick}
                    onLoad={() => setIsLoading(false)}
                    onError={() => { setIsLoading(false); setError(true); }}
                    alt={t('chat.internal.message.imageAlt')}
                />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Message content renderer (text / image / file / deleted)
// ---------------------------------------------------------------------------
const MessageContent = ({ msg, isMine, isMiniChat, onOpen }) => {
    const { t } = useTranslation();
    if (!msg) return null;

    const file = Array.isArray(msg?.file || msg?.message?.file)
        ? (msg.file || msg?.message?.file)[0]
        : (msg.file || msg?.message?.file);

    if (msg.deleted) {
        return (
            <div className="flex items-center gap-1.5 opacity-60">
                <Iconify icon="solar:trash-bin-minimalistic-linear" className={`w-3.5 h-3.5 ${isMine ? 'text-white/60' : 'text-gray-400'}`} />
                <span className={`italic ${isMiniChat ? 'text-[10px]' : 'text-[11px]'}`}>{t('chat.internal.message.deleted')}</span>
            </div>
        );
    }

    switch (msg.type) {
        case 'image':
            return (
                <ImageWithLoading
                    compact={isMiniChat}
                    className={`object-cover cursor-pointer rounded-lg hover:brightness-95 transition-all ${isMiniChat ? 'max-h-[120px] max-w-[160px]' : 'max-h-[220px] max-w-[280px]'}`}
                    src={`${Config.urlWs || ''}/api/files/${msg.content}`}
                    onClick={() => onOpen(msg)}
                />
            );

        case 'file':
            return (
                <div className={`flex items-center gap-2.5 ${isMiniChat ? 'mt-0.5' : 'mt-1'}`}>
                    <div className={`
                        flex items-center justify-center shrink-0 rounded-lg
                        ${isMiniChat ? 'w-7 h-7' : 'w-9 h-9'}
                        ${isMine ? 'bg-white/15' : 'bg-violet-50 dark:bg-violet-500/10'}
                    `}>
                        <Iconify
                            icon="solar:document-bold-duotone"
                            className={`${isMiniChat ? 'w-3.5 h-3.5' : 'w-4.5 h-4.5'} ${isMine ? 'text-white/80' : 'text-violet-500 dark:text-violet-400'}`}
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${isMiniChat ? 'text-[10px]' : 'text-[12px]'} ${isMine ? 'text-white' : 'text-gray-700 dark:text-[#E4E4E7]'}`}>
                            {file ? file.name : t('chat.internal.message.file')}
                        </p>
                        <p className={`${isMiniChat ? 'text-[8px]' : 'text-[10px]'} ${isMine ? 'text-white/50' : 'text-gray-400 dark:text-[#71717A]'}`}>
                            {t('chat.internal.message.fileAttached')}
                        </p>
                    </div>
                    <Button
                        onClick={() => window.open(`${Config.urlWs || ''}/api/files/${msg.content}`, '_blank')}
                        size="icon"
                        variant="ghost"
                        className={`
                            ${isMiniChat ? 'w-6 h-6' : 'w-7 h-7'} rounded-lg shrink-0
                            ${isMine
                                ? 'bg-white/10 hover:bg-white/20 text-white'
                                : 'bg-gray-100 dark:bg-[#2A2A2A] hover:bg-gray-200 dark:hover:bg-[#333] text-gray-500 dark:text-[#ABABAD]'
                            }
                        `}
                    >
                        <Iconify icon="solar:download-minimalistic-bold" width={isMiniChat ? 12 : 14} />
                    </Button>
                </div>
            );

        default:
            return (
                <p
                    className={`break-words whitespace-pre-wrap ${isMiniChat ? 'text-[11px] leading-relaxed' : 'text-[13px] leading-[1.5]'}`}
                    dangerouslySetInnerHTML={{ __html: msg.content && convertUrls(msg.content) }}
                />
            );
    }
};

// ---------------------------------------------------------------------------
// Actions dropdown (reply / delete)
// ---------------------------------------------------------------------------
const MessageActions = ({ isMine, message, onReply, onDelete }) => {
    const { t } = useTranslation();
    return (
    <DropdownMenu>
        <DropdownMenuTrigger asChild>
            <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-150 data-[state=open]:opacity-100 hover:bg-gray-100 dark:hover:bg-[#2A2A2A]"
            >
                <Iconify icon="solar:menu-dots-bold" className="h-3.5 w-3.5 text-gray-400 dark:text-[#71717A]" />
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={isMine ? "end" : "start"} className="w-36 rounded-xl p-1">
            <DropdownMenuItem onClick={() => onReply(message)} className="gap-2 cursor-pointer rounded-lg text-[12px]">
                <Iconify icon="solar:reply-bold" width={15} className="text-gray-500" />
                {t('chat.internal.message.reply')}
            </DropdownMenuItem>
            {isMine && (
                <DropdownMenuItem onClick={() => onDelete(message._id)} className="gap-2 cursor-pointer rounded-lg text-[12px] text-red-500 focus:text-red-600">
                    <Iconify icon="solar:trash-bin-minimalistic-bold" width={15} />
                    {t('common.buttons.delete')}
                </DropdownMenuItem>
            )}
        </DropdownMenuContent>
    </DropdownMenu>
    );
};

// ---------------------------------------------------------------------------
// Main message item
// ---------------------------------------------------------------------------
const ChatInternoMessageItem = ({ message, previous, next, onOpen, onDelete, onReply, isMiniChat, isReply }) => {
    let { author, content, date, replyMsg } = message;
    const { user } = useAuth();

    if (!author) author = { firstName: 'Deleted', lastName: 'User' };
    if (previous && !previous.author) previous.author = { firstName: 'Deleted', lastName: 'User' };
    if (next && !next.author) next.author = { firstName: 'Deleted', lastName: 'User' };

    const isMine = user.id === author._id;

    let attachPrevious = false, attachNext = false;
    if (previous && Math.abs(moment(previous.date).diff(moment(date), 'minutos')) < 3 && author._id === previous.author._id)
        attachPrevious = true;
    if (next && Math.abs(moment(next.date).diff(moment(date), 'minutos')) < 3 && author._id === next.author._id)
        attachNext = true;

    const noEmoji = content ? content.replace(emojiRegex(), '') : '';
    const isOnlyEmoji = !noEmoji.replace(/[\s\n]/gm, '');

    const bubbleClasses = isMine
        ? 'bg-violet-600 text-white rounded-2xl rounded-tr-sm shadow-sm shadow-violet-600/15'
        : 'bg-white dark:bg-[#1E1E1E] text-gray-800 dark:text-[#E4E4E7] rounded-2xl rounded-tl-sm border border-gray-100/80 dark:border-[#2A2A2A] shadow-sm';

    const showAuthor = !isReply && !isMine && !attachPrevious;
    const topSpacing = attachPrevious ? (isMiniChat ? 'mt-0.5' : 'mt-[2px]') : (isMiniChat ? 'mt-1' : 'mt-2.5');

    return (
        <div className={`flex ${topSpacing} ${isMiniChat ? 'px-1' : 'px-2'} ${isMine ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex flex-col ${isMiniChat ? 'max-w-[88%]' : 'max-w-[78%] md:max-w-[60%]'} ${isMine ? 'items-end' : 'items-start'}`}>

                {/* Author name */}
                {showAuthor && (
                    <span className={`${isMiniChat ? 'text-[9px] ml-2 mb-0.5' : 'text-[11px] ml-3 mb-1'} font-semibold text-gray-400 dark:text-[#71717A]`}>
                        {author.nomeFantasia}
                    </span>
                )}

                {/* Bubble + actions row */}
                <div className={`relative group flex ${isMine ? 'flex-row-reverse' : 'flex-row'} items-end gap-0.5`}>

                    {/* Bubble */}
                    <div className={`${isMiniChat ? 'px-2.5 py-1.5' : 'px-3.5 py-2'} ${bubbleClasses} relative`}>

                        {/* Reply quote */}
                        {replyMsg && (
                            <div className={`
                                ${isMiniChat ? 'p-1.5 mb-1.5 rounded-md' : 'px-2.5 py-1.5 mb-2 rounded-lg'}
                                border-l-2
                                ${isMine
                                    ? 'bg-white/10 border-white/30'
                                    : 'bg-gray-50 dark:bg-[#161616] border-violet-400/40'
                                }
                            `}>
                                <MessageContent msg={replyMsg} isMine={isMine} isMiniChat={isMiniChat} onOpen={onOpen} />
                            </div>
                        )}

                        {/* Main content */}
                        {isOnlyEmoji ? (
                            <span className={isMiniChat ? 'text-2xl' : 'text-3xl'}>{content}</span>
                        ) : (
                            <MessageContent msg={message} isMine={isMine} isMiniChat={isMiniChat} onOpen={onOpen} />
                        )}

                        {/* Inline timestamp */}
                        <div className={`
                            flex items-center gap-1 mt-1
                            ${isMine ? 'justify-end' : 'justify-start'}
                        `}>
                            <span className={`
                                ${isMiniChat ? 'text-[8px]' : 'text-[10px]'}
                                ${isMine ? 'text-white/40' : 'text-gray-300 dark:text-[#555]'}
                            `}>
                                {fToNow(date)}
                            </span>
                        </div>
                    </div>

                    {/* Actions */}
                    {!isReply && !isMiniChat && (
                        <MessageActions isMine={isMine} message={message} onReply={onReply} onDelete={onDelete} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatInternoMessageItem;
